import { useState } from "react";
import { Reorder, motion } from "framer-motion";
import { GripVertical, Hotel, Compass, Car, UtensilsCrossed, Camera, Palmtree, PenLine } from "lucide-react";

const INITIAL = [
  { title: "Airport → Resort", sub: "Private Transfer", icon: "transfer" },
  { title: "Resort · Breakfast", sub: "Free Day", icon: "free" },
  { title: "Island Hopping", sub: "Shared Experience", icon: "activity" },
  { title: "Sunset Cruise", sub: "Private", icon: "activity" },
  { title: "Spa Experience", sub: "Add-on", icon: "activity" },
  { title: "Beach & Leisure", sub: "Free Day", icon: "free" },
  { title: "Resort → Airport", sub: "Private Transfer", icon: "transfer" },
];

const ADDONS = [
  { label: "Hotel", icon: Hotel },
  { label: "Activity", icon: Compass },
  { label: "Transfer", icon: Car },
  { label: "Meal", icon: UtensilsCrossed },
  { label: "Sightseeing", icon: Camera },
  { label: "Free Day", icon: Palmtree },
  { label: "Custom Item", icon: PenLine },
];

const ICONS = { transfer: Car, activity: Compass, free: Palmtree, hotel: Hotel, meal: UtensilsCrossed, sight: Camera, custom: PenLine };

export default function SimPackageBuilder() {
  const [days, setDays] = useState(INITIAL);

  const add = (a) => {
    const key = a.label === "Free Day" ? "free" : a.label === "Custom Item" ? "custom" : a.label.toLowerCase().replace("sightseeing", "sight");
    setDays((d) => [...d, { title: `New ${a.label}`, sub: a.label, icon: key }]);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]" data-testid="sim-package-builder">
      <div className="rounded-2xl border border-sand bg-white p-4">
        <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone2">Add component</p>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
          {ADDONS.map((a) => (
            <button
              key={a.label}
              onClick={() => add(a)}
              data-testid={`sim-pb-add-${a.label.toLowerCase().replace(/\s/g, "-")}`}
              className="flex items-center gap-2 rounded-lg border border-sand px-3 py-2 text-left text-xs font-semibold text-ink transition-colors duration-300 hover:border-terra hover:bg-terra/5"
            >
              <a.icon className="h-4 w-4 text-terra" /> {a.label}
            </button>
          ))}
        </div>
        <p className="mt-3 hidden text-[11px] leading-relaxed text-stone2 lg:block">
          Click to add. Drag days on the right to reorder the itinerary.
        </p>
      </div>

      <div className="rounded-2xl border border-sand bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-serif text-lg text-ink">Maldives Escape</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone2">{days.length} days · {days.length - 1} nights · sample data</p>
          </div>
          <span className="rounded-full bg-parch px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-stone2">drag to reorder</span>
        </div>
        <Reorder.Group axis="y" values={days} onReorder={setDays} className="space-y-2">
          {days.map((d, i) => {
            const Icon = ICONS[d.icon] || Compass;
            return (
              <Reorder.Item
                key={d.title + d.sub}
                value={d}
                data-testid={`sim-pb-day-${i + 1}`}
                className="flex cursor-grab items-center gap-3 rounded-xl border border-sand bg-paper px-3.5 py-3 active:cursor-grabbing"
                whileDrag={{ scale: 1.02, boxShadow: "0 18px 35px -18px rgba(11,31,51,0.4)" }}
              >
                <GripVertical className="h-4 w-4 shrink-0 text-stone2/50" />
                <span className="w-14 shrink-0 font-mono text-[10px] font-bold uppercase tracking-wider text-terra">Day {String(i + 1).padStart(2, "0")}</span>
                <Icon className="h-4 w-4 shrink-0 text-ink" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{d.title}</span>
                  <span className="block text-[11px] text-stone2">{d.sub}</span>
                </span>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
        <motion.p layout className="mt-4 rounded-lg bg-terra/10 px-3.5 py-2 text-xs font-semibold text-ink" data-testid="sim-pb-summary">
          Itinerary updated — {days.length} days ready to price and publish.
        </motion.p>
      </div>
    </div>
  );
}
